import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
} from '@tanstack/react-table';
import {
  Table,
  Form,
  Button,
  Container,
  Row,
  Col,
} from 'react-bootstrap';

const BacklogJobErrorsTable = ({ data = [], isLoading = false,  getUserEmail = (uuid) => uuid }) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState([
    { id: 'timestamp', desc: true } // Sort by timestamp descending by default
  ]);
  
  // Define columns
  const columnHelper = createColumnHelper();
  
  const columns = useMemo(
    () => [
      columnHelper.accessor('user', {
        header: 'User',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('property', {
        header: 'Property',
        cell: (info) => (
          <span className="text-break">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('action', {
        header: 'Action',
        cell: (info) => (
          <span Style={info.getValue() === 'add' ? 'color:#1abc9c;' : 'color:#e74c3c;'}>
            {info.getValue() === 'add' ? 'Add' : 'Remove'}
          </span>
        ),
      }),
      columnHelper.accessor('permissions', {
        header: 'Permissions',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('issue', {
        header: 'Issue',
        cell: (info) => (
          <span id="jobissue" Style="color:#e74c3c;">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('requestor', {
        header: 'Requested By',
        cell: (info) => {
          const uuid = info.getValue();
          return getUserEmail ? getUserEmail(uuid) : (uuid || '-');
        },
      }),
      // columnHelper.accessor('requestor', {
      //   header: 'Requested By',
      //   cell: (info) => info.getValue() || 'Unknown',
      // }),
      columnHelper.accessor('timestamp', {
        header: 'Date',
        cell: (info) => {
          const timestamp = info.getValue();
          if (!timestamp) return '-';
          
          // Timestamp is a formatted string like "12/11/2025, 21:13:03"
          // Just display it as-is
          return timestamp;
        },
      }),
    ],
    [getUserEmail],
    [columnHelper]
  );
  
  // Create table instance
  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  
  if (data.length === 0) {
    return (
      <div className="text-center p-5 bg-light rounded">
        <p className="text-muted mb-0">No errors found</p>
        <small className="text-muted">
          All user management operations completed successfully
        </small>
      </div>
    );
  }
  
  return (
    <Container fluid className="px-0">
      {/* Pagination controls - Top */}
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={6} className="mb-2 mb-md-0">
          <div className="d-flex align-items-center">
            <span className="me-2 text-muted">Show &nbsp;</span>
            <Form.Select
              value={pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
              style={{ width: 'auto'}}
            >
              {[10, 25, 50, 100, 250, 500].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Form.Select>
            <span className="ms-2 text-muted">&nbsp; entries</span>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="d-flex justify-content-md-end">
            <span className="me-3 align-self-center text-muted">
              Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>&nbsp;
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </Col>
      </Row>
      
      {/* Table */}
      <div className="table-responsive">
        <Table striped bordered hover id="jobError">
          <thead style={{ backgroundColor: '#343a40', color: 'white' }}>
          {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th 
                    key={header.id}
                    style={{ 
                      width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span style={{ fontSize: '0.8em' }}>
                          {header.column.getIsSorted() === 'asc' ? '▲' : 
                           header.column.getIsSorted() === 'desc' ? '▼' : 
                           '⇅'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      
      {/* Pagination controls - Bottom */}
      <Row className="mt-3 align-items-center">
        <Col xs={12} md={6} className="mb-2 mb-md-0" Style="text-align: justify;">
          <span className="text-muted">
            Showing {table.getRowModel().rows.length} of {data.length} entries
          </span>
        </Col>
        <Col xs={12} md={6}>
          <div className="d-flex justify-content-md-end">
            <span className="me-3 align-self-center text-muted" Style="color:var(--dark);">
              Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="me-1"
            >
              Previous
            </Button>&nbsp;
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default BacklogJobErrorsTable;







// import React, { useState, useMemo } from 'react';
// import {
//   useReactTable,
//   getCoreRowModel,
//   flexRender,
//   getPaginationRowModel,
//   getSortedRowModel,
//   createColumnHelper,
// } from '@tanstack/react-table';
// import {
//   Table,
//   Form,
//   Button,
//   Container,
//   Row,
//   Col,
//   Spinner,
// } from 'react-bootstrap';

// const BacklogJobErrorsTable = ({ data = [], isLoading = false }) => {
//   const [pagination, setPagination] = useState({
//     pageIndex: 0,
//     pageSize: 10,
//   });
//   const [sorting, setSorting] = useState([
//     { id: 'timestamp', desc: true } // Sort by timestamp descending by default
//   ]);
  
//   // Define columns
//   const columnHelper = createColumnHelper();
  
//   const columns = useMemo(
//     () => [
//       columnHelper.accessor('user', {
//         header: 'User',
//         cell: (info) => info.getValue(),
//       }),
//       columnHelper.accessor('property', {
//         header: 'Property',
//         cell: (info) => (
//           <span className="text-break">{info.getValue()}</span>
//         ),
//       }),
//       columnHelper.accessor('action', {
//         header: 'Action',
//         cell: (info) => (
//           <span Style={info.getValue() === 'add' ? 'color:#1abc9c;' : 'color:#e74c3c;'}>
//             {info.getValue() === 'add' ? 'Add' : 'Remove'}
//           </span>
//         ),
//       }),
//       columnHelper.accessor('permissions', {
//         header: 'Permissions',
//         cell: (info) => info.getValue() || '-',
//       }),
//       columnHelper.accessor('issue', {
//         header: 'Issue',
//         cell: (info) => (
//           <span Style="color:#e74c3c;">{info.getValue()}</span>
//         ),
//       }),
//       columnHelper.accessor('requestor', {
//         header: 'Requested By',
//         cell: (info) => info.getValue() || 'Unknown',
//       }),
//       columnHelper.accessor('timestamp', {
//         header: 'Timestamp',
//         cell: (info) => {
//           const timestamp = info.getValue();
//           if (!timestamp) return '-';
          
//           // Timestamp is a formatted string like "12/11/2025, 21:13:03"
//           // Just display it as-is
//           return timestamp;
//         },
//       }),
//     ],
//     [columnHelper]
//   );
  
//   // Create table instance
//   const table = useReactTable({
//     data,
//     columns,
//     state: {
//       pagination,
//       sorting,
//     },
//     onPaginationChange: setPagination,
//     onSortingChange: setSorting,
//     getCoreRowModel: getCoreRowModel(),
//     getPaginationRowModel: getPaginationRowModel(),
//     getSortedRowModel: getSortedRowModel(),
//   });
  
//   if (isLoading) {
//     return (
//       <div className="text-center p-5">
//         <Spinner animation="border" />
//         <p className="mt-2">Loading error log...</p>
//       </div>
//     );
//   }
  
//   if (data.length === 0) {
//     return (
//       <div className="text-center p-5 bg-light rounded">
//         <p className="text-muted mb-0">No errors found</p>
//         <small className="text-muted">
//           All user management operations completed successfully
//         </small>
//       </div>
//     );
//   }
  
//   return (
//     <Container fluid className="px-0">
//       {/* Pagination controls - Top */}
//       <Row className="mb-3 align-items-center">
//         <Col xs={12} md={6} className="mb-2 mb-md-0">
//           <div className="d-flex align-items-center">
//             <span className="me-2 text-muted">Show &nbsp;</span>
//             <Form.Select
//               value={pagination.pageSize}
//               onChange={(e) => {
//                 table.setPageSize(Number(e.target.value));
//               }}
//               style={{ width: 'auto'}}
//             >
//               {[10, 25, 50, 100, 250, 500].map((pageSize) => (
//                 <option key={pageSize} value={pageSize}>
//                   {pageSize}
//                 </option>
//               ))}
//             </Form.Select>
//             <span className="ms-2 text-muted">&nbsp; entries</span>
//           </div>
//         </Col>
//         <Col xs={12} md={6}>
//           <div className="d-flex justify-content-md-end">
//             <span className="me-3 align-self-center text-muted">
//               Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
//             </span>
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.previousPage()}
//               disabled={!table.getCanPreviousPage()}
//             >
//               Previous
//             </Button>&nbsp;
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.nextPage()}
//               disabled={!table.getCanNextPage()}
//             >
//               Next
//             </Button>
//           </div>
//         </Col>
//       </Row>
//       {/* <Row className="mb-3 align-items-center">
//         <Col xs={12} md={6} className="mb-2 mb-md-0">
//           <div className="d-flex align-items-center">
//             <span className="me-2">Show</span>
//             <Form.Select
//               value={pagination.pageSize}
//               onChange={(e) => {
//                 table.setPageSize(Number(e.target.value));
//               }}
//               style={{ width: 'auto' }}
//             >
//               {[10, 25, 50, 100, 250, 500].map((pageSize) => (
//                 <option key={pageSize} value={pageSize}>
//                   {pageSize}
//                 </option>
//               ))}
//             </Form.Select>
//             <span className="ms-2">entries</span>
//           </div>
//         </Col>
//         <Col xs={12} md={6}>
//           <div className="d-flex justify-content-md-end">
//             <span className="me-3 align-self-center">
//               Page {pagination.pageIndex + 1} of {table.getPageCount() || 1}
//             </span>
//             <Button
//               variant="outline-secondary"
//               size="sm"
//               onClick={() => table.previousPage()}
//               disabled={!table.getCanPreviousPage()}
//               className="me-1"
//             >
//               Previous
//             </Button>
//             <Button
//               variant="outline-secondary"
//               size="sm"
//               onClick={() => table.nextPage()}
//               disabled={!table.getCanNextPage()}
//             >
//               Next
//             </Button>
//           </div>
//         </Col>
//       </Row> */}
      
//       {/* Table */}
//       <div className="table-responsive">
//         <Table striped bordered hover>
//           <thead style={{ backgroundColor: '#343a40', color: 'white' }}>
//           {table.getHeaderGroups().map((headerGroup) => (
//               <tr key={headerGroup.id}>
//                 {headerGroup.headers.map((header) => (
//                   <th 
//                     key={header.id}
//                     style={{ 
//                       width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
//                       textAlign: 'center',
//                       verticalAlign: 'middle',
//                       cursor: header.column.getCanSort() ? 'pointer' : 'default',
//                       userSelect: 'none',
//                     }}
//                     onClick={header.column.getToggleSortingHandler()}
//                   >
//                     <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
//                       {header.isPlaceholder
//                         ? null
//                         : flexRender(header.column.columnDef.header, header.getContext())}
//                       {header.column.getCanSort() && (
//                         <span style={{ fontSize: '0.8em' }}>
//                           {header.column.getIsSorted() === 'asc' ? '▲' : 
//                            header.column.getIsSorted() === 'desc' ? '▼' : 
//                            '⇅'}
//                         </span>
//                       )}
//                     </div>
//                   </th>
//                 ))}
//               </tr>
//             ))}
//             {/* {table.getHeaderGroups().map((headerGroup) => (
//               <tr key={headerGroup.id}>
//                 {headerGroup.headers.map((header) => (
//                   <th
//                     key={header.id}
//                     onClick={header.column.getToggleSortingHandler()}
//                     style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}
//                   >
//                     {flexRender(
//                       header.column.columnDef.header,
//                       header.getContext()
//                     )}
//                     {header.column.getIsSorted() && (
//                       <span className="ms-1">
//                         {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
//                       </span>
//                     )}
//                   </th>
//                 ))}
//               </tr>
//             ))} */}
//           </thead>
//           <tbody>
//             {table.getRowModel().rows.map((row) => (
//               <tr key={row.id}>
//                 {row.getVisibleCells().map((cell) => (
//                   <td key={cell.id}>
//                     {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                   </td>
//                 ))}
//               </tr>
//             ))}
//           </tbody>
//         </Table>
//       </div>
      
//       {/* Pagination controls - Bottom */}
//       <Row className="mt-3 align-items-center">
//         <Col xs={12} md={6} className="mb-2 mb-md-0" Style="text-align: justify;">
//           <span className="text-muted">
//             Showing {table.getRowModel().rows.length} of {data.length} entries
//           </span>
//         </Col>
//         <Col xs={12} md={6}>
//           <div className="d-flex justify-content-md-end">
//             <span className="me-3 align-self-center text-muted" Style="color:var(--dark);">
//               Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
//             </span>
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.previousPage()}
//               disabled={!table.getCanPreviousPage()}
//               className="me-1"
//             >
//               Previous
//             </Button>&nbsp;
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.nextPage()}
//               disabled={!table.getCanNextPage()}
//             >
//               Next
//             </Button>
//           </div>
//         </Col>
//       </Row>
//       {/* <Row className="mt-3 align-items-center">
//         <Col xs={12} md={6} className="mb-2 mb-md-0">
//           <span className="text-muted">
//             Showing {table.getRowModel().rows.length} of {data.length} entries
//           </span>
//         </Col>
//         <Col xs={12} md={6}>
//           <div className="d-flex justify-content-md-end">
//             <span className="me-3 align-self-center">
//               Page {pagination.pageIndex + 1} of {table.getPageCount() || 1}
//             </span>
//             <Button
//               variant="outline-secondary"
//               size="sm"
//               onClick={() => table.previousPage()}
//               disabled={!table.getCanPreviousPage()}
//               className="me-1"
//             >
//               Previous
//             </Button>
//             <Button
//               variant="outline-secondary"
//               size="sm"
//               onClick={() => table.nextPage()}
//               disabled={!table.getCanNextPage()}
//             >
//               Next
//             </Button>
//           </div>
//         </Col>
//       </Row> */}
//     </Container>
//   );
// };

// // UserMkrmErrorTable.js
// export default BacklogJobErrorsTable;